import React, { useState } from 'react';
import { Bell, Loader2, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { toast } from 'sonner';
import { track } from '../lib/analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * NotifyMeButton — pre-launch capture that hooks straight into the
 * newsletter API. Each click is tagged with the product so you can
 * email a per-product launch list on July 17, 2026.
 */
export const NotifyMeButton = ({ productId, productName }) => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          source: `shop_notify_${productId}`,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Could not subscribe.');
      setDone(true);
      track('notify_me_clicked', {
        product_id: String(productId),
        product_name: productName,
      });
      toast.success(`We'll ping you when ${productName} drops.`);
      setTimeout(() => {
        setOpen(false);
        setTimeout(() => {
          setDone(false);
          setEmail('');
        }, 300);
      }, 1500);
    } catch (err) {
      toast.error(err.message || 'Network error. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button
        type="button"
        onClick={() => setOpen(true)}
        className="w-full bg-amber-500/15 hover:bg-amber-500/25 text-amber-300 hover:text-amber-200 border border-amber-400/40 hover:border-amber-400/70 font-bold tracking-wider uppercase text-xs py-2.5 transition-all"
        data-testid={`notify-me-${productId}`}
      >
        <Bell size={14} className="mr-2" />
        Notify Me When It Drops
      </Button>
      <DialogContent
        className="bg-gunmetal-900 border border-amber-400/40 text-steel-100 max-w-md"
        data-testid="notify-me-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-black tracking-widest uppercase text-amber-400 flex items-center gap-2">
            <Bell size={18} />
            First In Line
          </DialogTitle>
          <DialogDescription className="text-steel-400 pt-1">
            Drop your email and we'll send you a link the second{' '}
            <strong className="text-amber-300">{productName}</strong> goes live on
            July 17, 2026.
          </DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="py-6 text-center" data-testid="notify-me-success">
            <Check className="mx-auto text-amber-400 mb-2" size={42} />
            <p className="text-steel-100 font-bold">You're locked in.</p>
            <p className="text-steel-400 text-sm">See you on launch day.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="pt-2 space-y-3">
            <Input
              type="email"
              required
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-gunmetal-800 border-steel-700 text-steel-100 placeholder:text-steel-500"
              data-testid="notify-me-email"
              autoFocus
            />
            <DialogFooter>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-amber-500 hover:bg-amber-400 text-gunmetal-950 font-black tracking-widest uppercase w-full"
                data-testid="notify-me-submit"
              >
                {submitting ? (
                  <><Loader2 className="mr-2 animate-spin" size={14} /> Locking in…</>
                ) : (
                  <><Bell className="mr-2" size={14} /> Notify Me</>
                )}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default NotifyMeButton;
